import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TypeBadge } from '@/components/ui/type-badge';
import { SectorBadge } from '@/components/ui/sector-badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Briefcase, Users, GraduationCap, ArrowRight, Globe, 
  TrendingUp, Award, Heart, MapPin, DollarSign,
  Building2, UserCheck, Target, Sparkles
} from 'lucide-react';

interface JobOpportunity {
  id: string;
  title: string;
  type: 'job' | 'tender' | 'jv' | 'training' | 'service_offer';
  sector: 'mining' | 'processing' | 'infrastructure' | 'industrial' | 'services' | 'other';
  country: string;
  city: string | null;
  remote: boolean;
  eligibility: 'local' | 'diaspora_welcome' | 'international';
  company: { name: string };
}

interface Program {
  id: string;
  title: string;
  description: string;
  spa_pillar: string | null;
}

const ForProfessionals = () => {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState<JobOpportunity[]>([]);
  const [trainingPrograms, setTrainingPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      supabase
        .from('opportunities')
        .select('id, title, type, sector, country, city, remote, eligibility, company:companies(name)')
        .in('type', ['job', 'training'])
        .eq('status', 'open')
        .order('published_at', { ascending: false })
        .limit(6),
      supabase
        .from('spa_programs')
        .select('id, title, description, spa_pillar')
        .limit(4)
    ]).then(([jobsRes, programsRes]) => {
      setJobs((jobsRes.data as unknown as JobOpportunity[]) || []);
      setTrainingPrograms((programsRes.data as unknown as Program[]) || []);
      setLoading(false);
    });
  }, []);

  const opportunities = [
    {
      icon: Globe,
      title: t('forProfessionals.opportunities.diaspora.title'),
      description: t('forProfessionals.opportunities.diaspora.description'),
      color: 'bg-primary/10 text-primary',
      stats: '120+ Roles'
    },
    {
      icon: TrendingUp,
      title: t('forProfessionals.opportunities.skills.title'),
      description: t('forProfessionals.opportunities.skills.description'),
      color: 'bg-secondary/10 text-secondary',
      stats: '$50K-200K'
    },
    {
      icon: Users,
      title: t('forProfessionals.opportunities.remote.title'),
      description: t('forProfessionals.opportunities.remote.description'),
      color: 'bg-accent/10 text-accent-foreground',
      stats: '40% Remote'
    },
    {
      icon: GraduationCap,
      title: t('forProfessionals.opportunities.mentorship.title'),
      description: t('forProfessionals.opportunities.mentorship.description'),
      color: 'bg-muted text-muted-foreground',
      stats: '50+ Mentors'
    },
    {
      icon: Award,
      title: t('forProfessionals.opportunities.development.title'),
      description: t('forProfessionals.opportunities.development.description'),
      color: 'bg-primary/10 text-primary',
      stats: '25 Programs'
    },
    {
      icon: Heart,
      title: t('forProfessionals.opportunities.return.title'),
      description: t('forProfessionals.opportunities.return.description'),
      color: 'bg-secondary/10 text-secondary',
      stats: '80% Success'
    }
  ];

  const careerPaths = [
    { sector: 'mining', demand: 'High', salary: '$80K-150K', roles: '35' },
    { sector: 'processing', demand: 'Medium', salary: '$60K-120K', roles: '20' },
    { sector: 'infrastructure', demand: 'High', salary: '$70K-140K', roles: '45' },
    { sector: 'industrial', demand: 'Medium', salary: '$55K-110K', roles: '25' }
  ];

  const getDemandColor = (demand: string) => {
    return demand === 'High' ? 'text-green-600 bg-green-50' : 'text-yellow-600 bg-yellow-50';
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary to-secondary">
        <div className="container relative py-24 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-4xl text-center text-primary-foreground"
          >
            <h1 className="font-heading text-balance text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
              {t('forProfessionals.hero.title')}
            </h1>
            <p className="mt-6 text-lg text-primary-foreground/80 md:text-xl">
              {t('forProfessionals.hero.subtitle')}
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90" asChild>
                <Link to="/opportunities?type=job">
                  {t('forProfessionals.cta.findJobs')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 bg-white/10 text-white hover:bg-white/20" asChild>
                <Link to="/talent">{t('forProfessionals.cta.joinTalent')}</Link>
              </Button>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
      </section>

      {/* Career Pathways */}
      <section className="py-16">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold mb-4">{t('forProfessionals.careers.title')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t('forProfessionals.careers.description')}</p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2">
            {careerPaths.map((path, index) => (
              <motion.div
                key={path.sector}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
              >
                <Card className="transition-shadow hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="font-semibold text-lg capitalize">{t(`sectors.${path.sector}`)}</h3>
                      <Badge className={getDemandColor(path.demand)}>{path.demand}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">{t('forProfessionals.careers.salary')}</p>
                        <p className="font-semibold">{path.salary}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">{t('forProfessionals.careers.openRoles')}</p>
                        <p className="font-semibold">{path.roles} {t('forProfessionals.careers.positions')}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Opportunities */}
      <section className="bg-muted/30 py-16">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold mb-4">{t('forProfessionals.opportunities.title')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t('forProfessionals.opportunities.description')}</p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {opportunities.map((opportunity, index) => (
              <motion.div
                key={opportunity.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
              >
                <Card className="h-full transition-shadow hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full ${opportunity.color}`}>
                      <opportunity.icon className="h-6 w-6" />
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-lg font-semibold">{opportunity.title}</h3>
                      <Badge variant="secondary" className="text-xs">{opportunity.stats}</Badge>
                    </div>
                    <p className="text-muted-foreground">{opportunity.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Job Opportunities */}
      <section className="py-16">
        <div className="container">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="font-heading text-3xl font-bold mb-2">{t('forProfessionals.jobs.title')}</h2>
              <p className="text-muted-foreground">{t('forProfessionals.jobs.description')}</p>
            </div>
            <Button variant="outline" asChild>
              <Link to="/opportunities?type=job">
                {t('forProfessionals.jobs.viewAll')} <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {loading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-40 w-full" />)}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {jobs.map(job => (
                <Link key={job.id} to={`/opportunities/${job.id}`}>
                  <Card className="h-full transition-shadow hover:shadow-lg">
                    <CardContent className="p-5 space-y-3">
                      <div className="flex flex-wrap gap-2">
                        <TypeBadge type={job.type} />
                        <SectorBadge sector={job.sector} />
                        {job.eligibility === 'diaspora_welcome' && (
                          <Badge className="bg-blue-100 text-blue-800 text-xs">
                            {t('forProfessionals.jobs.diasporaWelcome')}
                          </Badge>
                        )}
                        {job.remote && (
                          <Badge variant="outline" className="text-xs">
                            {t('forProfessionals.jobs.remote')}
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-semibold line-clamp-2">{job.title}</h3>
                      <p className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Building2 className="h-3 w-3" />{job.company?.name}
                      </p>
                      <p className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {job.city ? `${job.city}, ` : ''}{job.country}
                        {job.remote && ' (Remote)'}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Professional Development */}
      <section className="bg-muted/30 py-16">
        <div className="container">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="font-heading text-3xl font-bold mb-2">{t('forProfessionals.development.title')}</h2>
              <p className="text-muted-foreground">{t('forProfessionals.development.description')}</p>
            </div>
            <Button variant="outline" asChild>
              <Link to="/programs">
                {t('forProfessionals.development.viewAll')} <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {loading ? (
            <div className="grid gap-6 md:grid-cols-2">
              {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 w-full" />)}
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {trainingPrograms.map(program => (
                <Card key={program.id} className="transition-shadow hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold line-clamp-2 flex-1">{program.title}</h3>
                      <Sparkles className="h-5 w-5 text-primary ml-3 shrink-0" />
                    </div>
                    <p className="text-muted-foreground text-sm line-clamp-3 mb-3">{program.description}</p>
                    {program.spa_pillar && (
                      <Badge variant="outline" className="text-xs">
                        {t(`spaPillars.${program.spa_pillar}`)}
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="container py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="rounded-2xl bg-gradient-to-r from-primary to-secondary p-8 text-center text-white md:p-12"
        >
          <h2 className="font-heading text-3xl font-bold md:text-4xl mb-4">
            {t('forProfessionals.cta.title')}
          </h2>
          <p className="mx-auto max-w-2xl text-white/80 mb-8">
            {t('forProfessionals.cta.description')}
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button size="lg" className="bg-white text-primary hover:bg-white/90" asChild>
              <Link to="/auth?mode=signup">{t('forProfessionals.cta.createProfile')}</Link>
            </Button>
            <Button size="lg" variant="link" className="text-white" asChild>
              <Link to="/opportunities">{t('forProfessionals.cta.exploreRoles')}</Link>
            </Button>
          </div>
        </motion.div>
      </section>
    </Layout>
  );
};

export default ForProfessionals;